import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { init } from 'i18next';
import { keyValuePair, property } from 'src/app/models';

@Component({
  selector: 'app-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss'],
  standalone: false
})
export class PaginatorComponent implements OnInit, OnChanges {
  readonly Math = Math;

  @Input() length: number = 0;
  @Input() pageSize: number = 10;
  @Input() pageIndex: number = null;
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100];
  @Input() hidePageSize: boolean = false;
  @Input() showFirstLastButtons: boolean = true;
  @Input() disabled: boolean = false;

  @Output() pageChange = new EventEmitter<PageEvent>();
  @Output() pageSizeChange = new EventEmitter<number>();

  pageSizeOptionsArray: property[] = null;
  inputPageNumber: number = 1;
  pagesToShow: number[] = [1, 2, 3];



  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pageSizeOptions) {
      this.pageSizeOptionsArray = (this.pageSizeOptions ?? [])
        .map(size => ({ id: size, name: size.toString() }));
    }

    if (changes.pageIndex) {
      const currentIndex = this.pageIndex ?? 0;
      this.inputPageNumber = currentIndex + 1;
    }

    if (changes.length || changes.pageSize || changes.pageIndex) {
      this.calculatePagesToShow();
    }
  }

  ngOnInit(): void {
    this.pageSizeOptionsArray = this.pageSizeOptions.map(size => { return { id: size, name: size.toString() }; });
    this.inputPageNumber = (this.pageIndex ?? 0) + 1;
    this.calculatePagesToShow();
  }


  onPageChange(event: PageEvent): void {
    this.calculatePagesToShow();
    this.pageChange.emit(event);
  }

  calculatePagesToShow(): void {
    this.pagesToShow = [];
    const totalPages = Math.ceil(this.length / this.pageSize);
    if (!totalPages) {
      return;
    }

    const currentPage = Math.min(Math.max(this.inputPageNumber ?? 1, 1), totalPages);
    let startPage: number;
    let endPage: number;

    if (totalPages <= 3) {
      startPage = 1;
      endPage = totalPages;
    } else if (currentPage <= 2) {
      startPage = 1;
      endPage = 3;
    } else if (currentPage >= totalPages - 1) {
      startPage = totalPages - 2;
      endPage = totalPages;
    } else {
      startPage = currentPage - 1;
      endPage = currentPage + 1;
    }

    for (let i = startPage; i <= endPage; i++) {
      this.pagesToShow.push(i);
    }
  }

  onPageSizeChange($event): void {
    if (this.disabled) {
      return;
    }

    const newSize = Number($event);
    if (!Number.isFinite(newSize) || newSize <= 0) {
      return;
    }

    this.pageSize = newSize;
    this.pageSizeChange.emit(newSize);
    this.goToPageIndex(0);
  }

  private goToPageIndex(newPageIndex: number): void {
    if (this.disabled) {
      return;
    }

    const totalPages = Math.ceil(this.length / this.pageSize);
    if (!totalPages) {
      return;
    }

    const clampedIndex = Math.min(Math.max(newPageIndex, 0), totalPages - 1);
    const previousPageIndex = this.pageIndex ?? 0;

    if (clampedIndex === previousPageIndex && this.inputPageNumber === clampedIndex + 1) {
      this.calculatePagesToShow();
      return;
    }

    this.pageIndex = clampedIndex;
    this.inputPageNumber = clampedIndex + 1;
    this.calculatePagesToShow();

    this.pageChange.emit({
      pageIndex: clampedIndex,
      pageSize: this.pageSize,
      length: this.length,
      previousPageIndex
    });
  }

  onPreviousPage(): void {
    this.goToPageIndex((this.pageIndex ?? 0) - 1);
  }

  onNextPage(): void {
    this.goToPageIndex((this.pageIndex ?? 0) + 1);
  }

  onFirstPage(): void {
    this.goToPageIndex(0);
  }

  onLastPage(): void {
    const totalPages = Math.ceil(this.length / this.pageSize);
    if (!totalPages) {
      return;
    }
    this.goToPageIndex(totalPages - 1);
  }

  onPage(page: number): void {
    if (page == null) {
      return;
    }
    this.goToPageIndex(page - 1);
  }

  onGoToPage(): void {
    const totalPages = Math.ceil(this.length / this.pageSize);
    if (!totalPages) {
      return;
    }

    const parsedPage = Number(this.inputPageNumber);
    const targetPage = Number.isFinite(parsedPage) ? Math.floor(parsedPage) : 1;
    const clampedPage = Math.min(Math.max(targetPage, 1), totalPages);
    this.goToPageIndex(clampedPage - 1);
  }
}
